import React, { useState, useEffect } from 'react';
import { FALLBACK_APPS, type AppType } from './components/ApplicationsList';
import { FALLBACK_BUSINESS_APPS } from './components/BusinessApplicationsList';
import {
  LayoutGrid,
  Send,
  Globe2,
  ShoppingBag,
  Receipt,
  Ticket,
  UtensilsCrossed,
  MessageCircle,
} from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import CGU from '../pages/CGU';
import Privacy from '../pages/Privacy';
import DataDelete from '../pages/DataDelete';
import Support from '../pages/Support';
import ServiceUsage from '../pages/cgu/ServiceUsage';
import RightsResponsibilities from '../pages/cgu/RightsResponsibilities';
import SecurityDataProtection from '../pages/cgu/SecurityDataProtection';
import { LinkHomePage, LinkOpenPage } from '../pages/LinkPages';
import PayFallbackPage from '../pages/PayFallbackPage';
import AppDeepLinkOpenPage from '../pages/AppDeepLinkOpenPage';
import { ShortLinkOpenPage } from '../pages/ShortLinkDomainPages';
import {
  isAppDeeplinkHostname,
  isShortLinkHostname,
} from './utils/burokHosts';
import DownloadButtons from './components/DownloadButtons';
import DownloadModal from './components/DownloadModal';
import { SITE_STATUS } from './config/siteStatus';
import ConstructionMode from './components/ConstructionMode';
import { useLanguage } from './context/LanguageContext';
import { normalizePublicAssetUrl } from './constants/storage';
import {
  EcosystemRotatingCarouselFigure,
  ECOSYSTEM_ROTATING_SLIDE_COUNT,
} from './components/EcosystemRotatingCarouselFigure';
import {
  SN,
  BJ,
  BF,
  CV,
  CI,
  GM,
  GH,
  GN,
  GW,
  LR,
  ML,
  NE,
  NG,
  SL,
  TG,
} from 'country-flag-icons/react/3x2';

/** Pays affichés dans le badge hero (ordre = drapeaux). */
const HERO_WEST_AFRICA_FLAGS: { code: string; label: string }[] = [
  { code: 'sn', label: 'Sénégal' },
  { code: 'bj', label: 'Bénin' },
  { code: 'bf', label: 'Burkina Faso' },
  { code: 'cv', label: 'Cap-Vert' },
  { code: 'ci', label: "Côte d'Ivoire" },
  { code: 'gm', label: 'Gambie' },
  { code: 'gh', label: 'Ghana' },
  { code: 'gn', label: 'Guinée' },
  { code: 'gw', label: 'Guinée-Bissau' },
  { code: 'lr', label: 'Libéria' },
  { code: 'ml', label: 'Mali' },
  { code: 'ne', label: 'Niger' },
  { code: 'ng', label: 'Nigéria' },
  { code: 'sl', label: 'Sierra Leone' },
  { code: 'tg', label: 'Togo' },
];

/** SVG 3:2 embarqués (Windows n’affiche pas les drapeaux emoji, seulement SN, CI, etc.). */
const HERO_WEST_AFRICA_FLAG_SVGS = {
  sn: SN,
  bj: BJ,
  bf: BF,
  cv: CV,
  ci: CI,
  gm: GM,
  gh: GH,
  gn: GN,
  gw: GW,
  lr: LR,
  ml: ML,
  ne: NE,
  ng: NG,
  sl: SL,
  tg: TG,
} as Record<string, typeof SN>;

/** Logos un peu plus grands (lisibilité des marques horizontales). */
const HERO_PARTNER_LOGO_LG =
  'max-h-10 w-auto max-w-[min(100%,10rem)] object-contain opacity-90 sm:max-h-11 sm:max-w-[12rem] md:max-h-12 md:max-w-[13rem]';

/** Paylink : taille au-dessus de LG. */
const HERO_PARTNER_LOGO_PAYLINK =
  'max-h-11 w-auto max-w-[min(100%,11.5rem)] object-contain opacity-90 sm:max-h-12 sm:max-w-[13.5rem] md:max-h-14 md:max-w-[15.5rem]';

const HERO_PARTNER_LOGOS: { src: string; alt: string; imgClassName?: string }[] = [
  { src: '/assets/goldrailogoblack.png', alt: 'Goldrail', imgClassName: HERO_PARTNER_LOGO_LG },
  { src: '/assets/openconnectblack.png', alt: 'OpenConnect', imgClassName: HERO_PARTNER_LOGO_LG },
  { src: '/assets/Paylink.png', alt: 'Paylink', imgClassName: HERO_PARTNER_LOGO_PAYLINK },
  { src: '/assets/YANGOTEXTUALBLACK.png', alt: 'Yango' },
  { src: '/assets/LinkButton.png', alt: 'Link Button', imgClassName: HERO_PARTNER_LOGO_LG },
  { src: '/assets/Visa-Logo.png', alt: 'Visa' },
  {
    src: '/assets/TEMPOBLOCKCHAINWHITE.png',
    alt: 'Tempo Blockchain',
    imgClassName:
      'max-h-6 w-auto max-w-[6rem] object-contain opacity-90 sm:max-h-7 sm:max-w-[7rem] md:max-h-7 md:max-w-[7.5rem]',
  },
];

/** Galerie « Catalogue varié » : alternance automatique entre les deux visuels. */
const GALLERY_CATALOGUE_IMAGES = [
  { src: '/assets/Burokimage1.jpeg', alt: 'Interface Burok — catalogue produits' },
  { src: '/assets/REEF4GRG.jpeg', alt: 'Sélection de produits sur Burok' },
] as const;

/** Captures `public/assets/imageappburok` — rotation dans la section « Aperçu de l’app ». */
const APP_BUROK_SHOWCASE_IMAGE_IDS = [
  2, 3, 5, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
  64, 65, 66, 67, 68, 70,
] as const;

const APP_BUROK_SHOWCASE_IMAGES = APP_BUROK_SHOWCASE_IMAGE_IDS.map((n) => ({
  src: `/assets/imageappburok/${n}.png`,
  alt: `Aperçu de l’application Burok (${n})`,
}));

/** Graffiti pleine opacité — arrière-plan section aperçu app (fond blanc). */
const APP_BUROK_SHOWCASE_GRAFITI: { src: string; className: string }[] = [
  {
    src: '/assets/grafiti/grafiti4.png',
    className:
      'absolute -right-2 top-[5%] w-[min(52vw,17rem)] max-w-none opacity-100 sm:-right-6 sm:top-[7%] sm:w-80 md:w-[22rem]',
  },
  {
    src: '/assets/grafiti/grafiti6.png',
    className:
      'absolute -bottom-28 -left-12 w-[min(65vw,22rem)] max-w-none opacity-100 sm:-bottom-24 sm:-left-8 sm:w-[26rem] md:w-[30rem]',
  },
  {
    src: '/assets/grafiti/grafiti8.png',
    className:
      'absolute right-[6%] top-[36%] hidden w-52 opacity-100 sm:block sm:w-60 md:right-[10%] md:w-72',
  },
];

const AGENTS_BUROK_SERVICE_ROWS: { label: string; Icon: typeof Send }[] = [
  { label: "Transfert d'argent", Icon: Send },
  { label: 'Remittance', Icon: Globe2 },
  { label: 'Shopping', Icon: ShoppingBag },
  { label: 'Paiement de factures', Icon: Receipt },
  { label: 'Billets de transport', Icon: Ticket },
  { label: 'Commande', Icon: UtensilsCrossed },
];

const AGENTS_BUROK_GHIBLI_IDS = [3, 4, 5, 6, 10, 11, 19] as const;

/** Décor graffiti — arrière-plan des sections Agent IA (public/assets/grafiti) */
const AGENTS_IA_SECTION1_GRAFITI: { src: string; className: string }[] = [
  {
    src: '/assets/grafiti/grafiti1.png',
    className:
      'absolute -right-6 top-[8%] w-[min(42vw,14rem)] max-w-none opacity-[0.22] sm:-right-10 sm:top-[10%] sm:w-72 md:w-80 md:opacity-[0.28]',
  },
  {
    src: '/assets/grafiti/grafiti4.png',
    className:
      'absolute -bottom-20 -left-8 w-[min(55vw,18rem)] max-w-none opacity-[0.18] sm:-bottom-16 sm:-left-4 sm:w-[22rem] sm:opacity-[0.24]',
  },
  {
    src: '/assets/grafiti/grafiti7.png',
    className:
      'absolute bottom-[18%] right-[5%] hidden w-48 opacity-[0.17] lg:block lg:w-56 lg:opacity-[0.22]',
  },
];

const AGENTS_IA_SECTION2_GRAFITI: { src: string; className: string }[] = [
  {
    src: '/assets/grafiti/grafiti2.png',
    className:
      'absolute -left-4 top-[5%] w-[min(48vw,15rem)] max-w-none opacity-[0.2] sm:-left-6 sm:top-[8%] sm:w-64 md:opacity-[0.26]',
  },
  {
    src: '/assets/grafiti/grafiti9.png',
    className:
      'absolute -right-8 bottom-[12%] w-[min(50vw,16rem)] max-w-none opacity-[0.19] sm:-right-4 sm:bottom-[15%] sm:w-72 sm:opacity-[0.23]',
  },
  {
    src: '/assets/grafiti/grafiti5.png',
    className:
      'absolute left-1/2 top-[40%] w-40 -translate-x-1/2 opacity-[0.14] sm:w-52 sm:opacity-[0.19]',
  },
];

// Page d'accueil magnifique avec gradient rouge-orange
const Home: React.FC = () => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'client' | 'business'>('client');
  const [activeMiniAppType, setActiveMiniAppType] = useState<AppType | 'All'>('All');
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [isMySpaceModalOpen, setIsMySpaceModalOpen] = useState(false);
  const [ecosystemRotateIndex, setEcosystemRotateIndex] = useState(0);
  const [catalogueGalleryImageIndex, setCatalogueGalleryImageIndex] = useState(0);
  const [appBurokShowcaseIndex, setAppBurokShowcaseIndex] = useState(0);

  const handleDownloadClick = () => {
    setIsDownloadModalOpen(true);
  };

  const handleMySpaceClick = () => {
    setIsMySpaceModalOpen(true);
  };

  const closeMySpaceModal = () => {
    setIsMySpaceModalOpen(false);
  };

  const handleAppClick = (app: any) => {
    setSelectedApp(app);
    setIsAppModalOpen(true);
  };

  const closeAppModal = () => {
    setIsAppModalOpen(false);
    setSelectedApp(null);
  };

  // Images pour l'animation
  const animatedImages = [
    '/assets/OtherImages/1.png',
    '/assets/OtherImages/2.png',
    '/assets/OtherImages/3.png',
    '/assets/OtherImages/4.png',
    '/assets/OtherImages/5.png',
    '/assets/OtherImages/6.png',
    '/assets/OtherImages/7.png'
  ];

  // Effet pour changer l'image avec crossfade fluide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % animatedImages.length);
    }, 3000); // 3 secondes pour une transition plus douce

    return () => clearInterval(interval);
  }, [animatedImages.length]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setEcosystemRotateIndex((i) => (i + 1) % ECOSYSTEM_ROTATING_SLIDE_COUNT);
    }, 3000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setCatalogueGalleryImageIndex((i) => (i + 1) % GALLERY_CATALOGUE_IMAGES.length);
    }, 4500);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setAppBurokShowcaseIndex((i) => (i + 1) % APP_BUROK_SHOWCASE_IMAGES.length);
    }, 3800);
    return () => window.clearInterval(id);
  }, []);

  // Vérifier si le site est en construction
  if (SITE_STATUS.isUnderConstruction) {
    return <ConstructionMode />;
  }

  const { language } = useLanguage();
  const isEnglish = language === 'en';

  const appDisplayName = (app: { name: string; name_en?: string | null }) =>
    isEnglish ? app.name_en ?? app.name : app.name;
  const appDisplayDescription = (app: { description: string; description_en?: string | null }) =>
    isEnglish ? app.description_en ?? app.description : app.description;

  const miniAppTypeLabel = (t: AppType | 'All') => {
    if (!isEnglish) {
      return t === 'All' ? 'Tous' : t;
    }
    if (t === 'All') return 'All';
    switch (t) {
      case 'Finance':
        return 'Finance';
      case 'Shopping':
        return 'Shopping';
      case 'Transport':
        return 'Transport';
      case 'Logement':
        return 'Housing';
      case 'Service':
        return 'Services';
      case 'Social':
        return 'Social';
      case 'Autres':
        return 'Other';
      default:
        return t;
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <div className="overflow-y-visible relative bg-white overflow-x-clip">
        <Header
          variant="overlay"
          onDownloadClick={handleDownloadClick}
          onMySpaceClick={handleMySpaceClick}
        />

        {/* Hero Section */}
        <section className="relative z-10 px-4 pt-24 pb-16 sm:px-6 lg:px-8 sm:pt-28">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-12 items-center lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.62fr)]">
              <div className="text-center lg:text-left">
                <div className="mb-8 flex flex-col items-center sm:mb-10 lg:items-start">
                  <div className="inline-block w-full max-w-[15.5rem] align-top sm:max-w-[16.25rem]">
                    <div className="inline-flex max-w-full items-center px-2.5 py-1.5 text-[11px] font-semibold leading-snug text-white rounded-full border border-white/25 shadow-md shadow-[#FF4500]/25 bg-gradient-to-r from-[#FF9500] via-[#FF4500] to-[#FF1493] sm:px-3 sm:text-xs sm:leading-tight">
                      <span className="max-w-full drop-shadow-sm text-balance">
                        🚀 The First Super App in West Africa
                      </span>
                    </div>
                    <div className="overflow-hidden relative mt-3 w-full">
                      <div className="flex w-max animate-scroll-flags items-center hover:[animation-play-state:paused]">
                      {[0, 1].map((dup) => (
                        <div
                          key={dup}
                          className="flex shrink-0 items-center gap-2.5 pr-4 sm:gap-3 sm:pr-5"
                          {...(dup === 1 ? ({ 'aria-hidden': true } as const) : {})}
                        >
                          {HERO_WEST_AFRICA_FLAGS.map(({ code, label }) => {
                            const FlagSvg = HERO_WEST_AFRICA_FLAG_SVGS[code];
                            if (!FlagSvg) return null;
                            return (
                              <span
                                key={`${dup}-${code}`}
                                title={label}
                                className="inline-block overflow-hidden rounded-md ring-1 ring-inset shadow-sm shrink-0 ring-black/10 sm:rounded-lg"
                              >
                                <FlagSvg
                                  aria-hidden
                                  className="block h-4 w-6 sm:h-5 sm:w-[30px]"
                                />
                                {dup === 0 ? <span className="sr-only">{label}</span> : null}
                              </span>
                            );
                          })}
                        </div>
                      ))}
                      </div>
                    </div>
                  </div>
                </div>

                <h1 className="mb-6 text-5xl font-bold leading-tight text-gray-900 md:text-6xl lg:text-7xl">
                  {isEnglish ? 'Shop. Move. Live.' : 'Boutique. Shopping. Bouger.'}
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#fc2207] to-[#fe5801]">
                    {isEnglish ? 'All-in-one' : 'Tout en un'}
                  </span>
                </h1>
                <p className="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-gray-600 lg:mx-0">
                  {isEnglish ? (
                    <>
                      The super app that does it all: shopping, wallet, mobility, food and services.
                      One clean interface. No app jungle. Your everyday life, one shortcut.
                    </>
                  ) : (
                    <>
                      La super app qui fait tout : shopping, wallet, mobilité, food et services, une
                      seule interface, zéro jungle d&apos;apps. Tout ton quotidien, un seul app.
                    </>
                  )}
                </p>
                <div className="flex justify-center lg:justify-start">
                  <button
                    type="button"
                    onClick={handleDownloadClick}
                    className="inline-block rounded-xl transition-transform duration-300 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#fc2207] focus-visible:ring-offset-2"
                    aria-label={isEnglish ? 'Download Burok' : 'Télécharger Burok'}
                  >
                    <img
                      src="/assets/BurokDownloadApp.png"
                      alt={
                        isEnglish
                          ? 'Download on Google Play and the App Store'
                          : 'Télécharger sur Google Play et sur l’App Store'
                      }
                      className="object-contain mx-auto w-full max-w-sm h-auto sm:max-w-md lg:mx-0"
                    />
                  </button>
                </div>
              </div>

              <div className="flex justify-center w-full min-w-0 lg:justify-end lg:pl-10 xl:pl-14 2xl:pl-20">
                <div
                  className="relative w-full lg:ml-auto lg:mr-0 lg:w-auto lg:max-w-[min(100%,52rem)] lg:max-h-[min(82vh,880px)] xl:max-h-[min(85vh,960px)] lg:translate-x-2 xl:translate-x-7 2xl:translate-x-14"
                  aria-label={isEnglish ? 'Animated Burok illustration' : 'Illustration animée de Burok'}
                >
                  <img
                    src="/assets/grafitirotation.png"
                    alt=""
                    aria-hidden
                    className="block h-auto w-full object-contain [animation:spin_18s_linear_infinite]"
                  />
                  <div className="pointer-events-none absolute inset-0 grid place-items-center">
                    <img
                      src="/assets/BurokGradient.png"
                      alt="Burok"
                      className="animate-burok-pop h-16 w-auto object-contain sm:h-20 md:h-24"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8 mt-12 border-t border-gray-200/90 sm:mt-14 sm:pt-10">
              <p className="mb-5 text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-500 sm:text-xs">
                {isEnglish ? 'Partners' : 'Partenaires'}
              </p>
              <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
                <div
                  className="absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-white to-transparent pointer-events-none sm:w-16"
                  aria-hidden
                />
                <div
                  className="absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none sm:w-16"
                  aria-hidden
                />
                <div className="overflow-hidden py-1">
                  <div className="flex w-max animate-scroll-partners items-center hover:[animation-play-state:paused]">
                    {[0, 1].map((dup) => (
                      <div
                        key={dup}
                        className="flex shrink-0 items-center gap-12 pr-12 sm:gap-16 sm:pr-16 md:gap-[4.5rem] md:pr-[4.5rem]"
                        {...(dup === 1 ? ({ 'aria-hidden': 'true' } as const) : {})}
                      >
                        {HERO_PARTNER_LOGOS.map(({ src, alt, imgClassName }) => (
                          <div
                            key={`${dup}-${src}`}
                            className="flex justify-center items-center h-12 shrink-0 sm:h-16 md:h-16"
                          >
                            <img
                              src={src}
                              alt={alt}
                              className={
                                imgClassName ??
                                'max-h-9 w-auto max-w-[min(100%,9rem)] object-contain opacity-90 sm:max-h-10 sm:max-w-[11rem] md:max-h-11 md:max-w-[12rem]'
                              }
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="overflow-hidden relative flex-1 bg-gradient-to-br from-[#fc2207] via-[#fe5801] to-[#ff8400]">
        {/* Background Elements */}
        <div className="overflow-hidden absolute inset-0">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#fe2e3c] rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#ff4a66] rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#ff8400] rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-pulse" style={{animationDelay: '4s'}}></div>
        </div>

      {/* Cartes à votre goût Section - MASQUÉE */}
      <section className="hidden relative z-10 px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">
              Cartes à votre goût
            </h2>
            <p className="mx-auto max-w-2xl text-xl text-white/80">
              Personnalisez votre expérience avec nos designs de cartes exclusifs et élégants
            </p>
          </div>
          
          {/* Horizontal Scrolling Cards Container */}
          <div className="relative">
            {/* Carousel Container */}
            <div className="overflow-hidden relative">
              <div className="flex gap-6 pb-8 animate-scroll">
                {/* Duplicate cards at the beginning for seamless loop */}
                {/* Noir Élégant - Duplicate at start */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#000000] to-[#333333] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000000] to-[#333333] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      {/* Card Header */}
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src={normalizePublicAssetUrl("https://assets.burok.app/myfiles/logos/Burok%20White.png")}
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      {/* QR Code - Positioned like in carddesign.tsx */}
                      <img 
                        src={normalizePublicAssetUrl("https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png")}
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src={normalizePublicAssetUrl("https://assets.burok.app/myfiles/logos/OMCircular.png")}
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src={normalizePublicAssetUrl("https://assets.burok.app/myfiles/logos/YasCircular.png")}
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src={normalizePublicAssetUrl("https://assets.burok.app/Files/Logos/WaveMobileMoney.png")}
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Noir Élégant</h3>
                    <p className="text-sm text-white/60">Design classique et sophistiqué</p>
                  </div>
                </div>

                {/* Noir Luxe - Duplicate at start */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#000000] to-[#3b195e] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000000] to-[#3b195e] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BuroklogoPurple.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Noir Luxe</h3>
                    <p className="text-sm text-white/60">Élégance et raffinement</p>
                  </div>
                </div>

                {/* Purple Dream - Duplicate at start */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#7928ca] to-[#cb6ce6] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#7928ca] to-[#cb6ce6] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BurokLogoBlack.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Purple Dream</h3>
                    <p className="text-sm text-white/60">Rêve et créativité</p>
                  </div>
                </div>

                {/* Rouge Passion - Duplicate at start */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#f80615] to-[#ff5757] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#f80615] to-[#ff5757] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BurokBlueLogo.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Rouge Passion</h3>
                    <p className="text-sm text-white/60">Énergie et dynamisme</p>
                  </div>
                </div>

                {/* Noir Élégant */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#000000] to-[#333333] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000000] to-[#333333] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      {/* Card Header */}
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/Burok%20White.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      {/* QR Code - Positioned like in carddesign.tsx */}
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Noir Élégant</h3>
                    <p className="text-sm text-white/60">Design classique et sophistiqué</p>
                  </div>
                </div>

                {/* Noir Luxe */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#000000] to-[#3b195e] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000000] to-[#3b195e] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BuroklogoPurple.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      


                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Noir Luxe</h3>
                    <p className="text-sm text-white/60">Élégance et raffinement</p>
                  </div>
                </div>

                {/* Purple Dream */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#7928ca] to-[#cb6ce6] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#7928ca] to-[#cb6ce6] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BurokLogoBlack.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Purple Dream</h3>
                    <p className="text-sm text-white/60">Rêve et créativité</p>
                  </div>
              </div>

                {/* Rouge Passion */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#f80615] to-[#ff5757] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#f80615] to-[#ff5757] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BurokBlueLogo.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Rouge Passion</h3>
                    <p className="text-sm text-white/60">Énergie et dynamisme</p>
                  </div>
                </div>

                {/* Rouge Éclatant */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#d50000] to-[#ff5252] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#d50000] to-[#ff5252] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BurokLogoBlack.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Rouge Éclatant</h3>
                    <p className="text-sm text-white/60">Intensité et passion</p>
                  </div>
                </div>

                {/* Vert Nature */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#1b5e20] to-[#8dc230] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#1b5e20] to-[#8dc230] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BurokLogoBlack.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Vert Nature</h3>
                    <p className="text-sm text-white/60">Harmonie et croissance</p>
                  </div>
                </div>

                {/* Rose Flamingo */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#d81b60] to-[#ff5757] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#d81b60] to-[#ff5757] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BurokLogoBlack.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Rose Flamingo</h3>
                    <p className="text-sm text-white/60">Élégance et douceur</p>
                  </div>
                </div>

                {/* Bleu Azur */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#0d47a1] to-[#38b6ff] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0d47a1] to-[#38b6ff] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/BurokLogoBlack.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
            </div>
            
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Bleu Azur</h3>
                    <p className="text-sm text-white/60">Sérénité et confiance</p>
                  </div>
                        </div>
                        
                {/* Bleu Océan Premium */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#0575E6] to-[#021B79] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0575E6] to-[#021B79] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/Files/Logos/Engagment%20Burok%20Mobile.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="px-2 py-1 font-mono text-xs rounded text-white/60 bg-yellow-500/20">PREMIUM</div>
                          </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Bleu Océan</h3>
                    <p className="text-sm text-white/60">Profondeur et mystère</p>
                  </div>
                </div>

                {/* Aurore Boréale Premium */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#00c6ff] via-[#0072ff] to-[#0072ff] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#00c6ff] via-[#0072ff] to-[#0072ff] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/Files/Logos/Engagment%20Burok%20Mobile.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="px-2 py-1 font-mono text-xs rounded text-white/60 bg-yellow-500/20">PREMIUM</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Aurore Boréale</h3>
                    <p className="text-sm text-white/60">Magie et émerveillement</p>
                  </div>
                </div>

                {/* Sunset Doré Premium */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#f5af19] to-[#f12711] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#f5af19] to-[#f12711] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/Files/Logos/Engagment%20Burok%20Mobile.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="px-2 py-1 font-mono text-xs rounded text-white/60 bg-yellow-500/20">PREMIUM</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Sunset Doré</h3>
                    <p className="text-sm text-white/60">Chaleur et élégance</p>
                  </div>
                </div>

                {/* Violet Royal Premium */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#8E2DE2] via-[#4A00E0] to-[#6A3093] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#8E2DE2] via-[#4A00E0] to-[#6A3093] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/Files/Logos/Engagment%20Burok%20Mobile.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="px-2 py-1 font-mono text-xs rounded text-white/60 bg-yellow-500/20">PREMIUM</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Violet Royal</h3>
                    <p className="text-sm text-white/60">Noblesse et mystère</p>
                  </div>
                </div>

                {/* Émeraude Premium */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#11998e] to-[#38ef7d] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#11998e] to-[#38ef7d] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/Files/Logos/Engagment%20Burok%20Mobile.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="px-2 py-1 font-mono text-xs rounded text-white/60 bg-yellow-500/20">PREMIUM</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Émeraude</h3>
                    <p className="text-sm text-white/60">Prestige et rareté</p>
                  </div>
                </div>

                {/* Nuit Électrique Premium */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#141e30] via-[#243b55] to-[#243b55] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#141e30] via-[#243b55] to-[#243b55] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/Files/Logos/Engagment%20Burok%20Mobile.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="px-2 py-1 font-mono text-xs rounded text-white/60 bg-yellow-500/20">PREMIUM</div>
                      </div>
                      
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Nuit Électrique</h3>
                    <p className="text-sm text-white/60">Mystère et sophistication</p>
                  </div>
                </div>

                {/* Duplicate cards for seamless loop */}
                {/* Noir Élégant - Duplicate */}
                <div className="flex-shrink-0 cursor-pointer group">
                  <div className="w-80 h-48 bg-gradient-to-br from-[#000000] to-[#333333] rounded-2xl p-6 relative overflow-hidden shadow-2xl hover:scale-105 transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000000] to-[#333333] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="flex relative z-10 flex-col h-full">
                      <div className="flex justify-between items-start mb-4">
                        <img 
                          src="https://assets.burok.app/myfiles/logos/Burok%20White.png" 
                          alt="Burok Logo" 
                          className="object-contain w-20 h-10"
                        />
                        <div className="font-mono text-xs text-white/60">CLASSIC</div>
                          </div>
                      
                      {/* QR Code - Positioned like in carddesign.tsx */}
                      <img 
                        src="https://assets.burok.app/myfiles/logos/PNGWhiteQRCODECARD.png" 
                        alt="QR Code" 
                        className="absolute right-0 top-1/2 w-24 h-24 opacity-90 transform -translate-y-1/2"
                      />
                      
                      {/* Payment Logos - Bottom left aligned with Burok logo */}
                      <div className="absolute bottom-2 left-2">
                        <div className="flex gap-2">
                          <img 
                            src="https://assets.burok.app/myfiles/logos/OMCircular.png" 
                            alt="Orange Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/myfiles/logos/YasCircular.png" 
                            alt="Yas" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                          <img 
                            src="https://assets.burok.app/Files/Logos/WaveMobileMoney.png" 
                            alt="Wave Mobile Money" 
                            className="w-8 h-8 rounded-full bg-white/10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <h3 className="text-xl font-bold text-white">Noir Élégant</h3>
                    <p className="text-sm text-white/60">Design classique et sophistiqué</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Scroll Indicators */}
            <div className="flex absolute left-0 top-1/2 justify-center items-center w-12 h-12 bg-gradient-to-r to-transparent rounded-r-full opacity-0 transition-opacity duration-300 transform -translate-y-1/2 from-white/20 hover:opacity-100">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="flex absolute right-0 top-1/2 justify-center items-center w-12 h-12 bg-gradient-to-l to-transparent rounded-l-full opacity-0 transition-opacity duration-300 transform -translate-y-1/2 from-white/20 hover:opacity-100">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Marques Partenaires - MASQUÉE */}
          <div className="hidden mt-20 text-center">
            <h3 className="mb-8 text-3xl font-bold tracking-tight text-white">Nos Partenaires de Confiance</h3>
            <div className="flex flex-wrap gap-12 justify-center items-center">
              <div className="transition-transform duration-300 group hover:scale-110">
                <img 
                  src="/assets/Brands/Stripe-Logo.png" 
                  alt="Stripe" 
                  className="h-12 opacity-100 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div className="transition-transform duration-300 group hover:scale-110">
                <img 
                  src="/assets/Brands/WalletConnect.png" 
                  alt="WalletConnect" 
                  className="h-12 opacity-100 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div className="transition-transform duration-300 group hover:scale-110">
                <img 
                  src="/assets/Brands/paydunya.png" 
                  alt="PayDunya" 
                  className="h-12 opacity-100 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div className="transition-transform duration-300 group hover:scale-110">
                <img 
                  src="/assets/Brands/OnRamper.png" 
                  alt="OnRamper" 
                  className="h-12 opacity-100 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div className="transition-transform duration-300 group hover:scale-110">
                <img 
                  src="/assets/Brands/MoonPay.png" 
                  alt="MoonPay" 
                  className="h-12 opacity-100 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div className="transition-transform duration-300 group hover:scale-110">
                <img 
                  src="/assets/Brands/MixxByYas.png" 
                  alt="Mixx by Yas" 
                  className="h-16 opacity-100 transition-opacity duration-300 group-hover:opacity-110"
                />
              </div>
              <div className="transition-transform duration-300 group hover:scale-110">
                <img 
                  src="/assets/Brands/Wavemobilelogo.png" 
                  alt="Wave Mobile" 
                  className="h-12 opacity-100 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div className="transition-transform duration-300 group hover:scale-110">
                <img 
                  src="/assets/Brands/b2b-orangemoney.png" 
                  alt="Orange Money B2B" 
                  className="h-12 opacity-100 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-12 text-center">
            <div className="inline-flex gap-2 items-center px-6 py-3 rounded-full border backdrop-blur-sm transition-all duration-300 cursor-pointer bg-white/10 border-white/20 hover:bg-white/20 group">
              <span className="font-medium text-white">Voir tous les designs</span>
              <svg className="w-4 h-4 transition-transform duration-300 text-white/60 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Écosystème Burok */}
      <section
        className="relative z-10 px-4 py-24 sm:px-6 lg:px-8 lg:py-28"
        aria-labelledby="ecosysteme-burok-heading"
      >
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto mb-10 max-w-2xl text-center lg:mb-14">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-white/60 sm:text-sm">
              {isEnglish ? 'One account, endless possibilities' : 'Un seul compte, mille possibilités'}
            </p>
            <h2
              id="ecosysteme-burok-heading"
              className="mb-5 text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-6xl"
            >
              L&apos;écosystème{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-white to-orange-100">
                Burok
              </span>
            </h2>
            <p className="text-lg leading-relaxed text-white/85 md:text-xl">
              {isEnglish
                ? 'A super app built for everyday life and business ambition. Shopping, services, food and pro tools in one smooth, local-first experience that scales.'
                : 'Une super app pensée pour la vie quotidienne et pour les ambitions business : shopping, services, food et outils pros réunis dans une expérience fluide, locale et évolutive.'}
            </p>
          </div>

          <div className="relative -mx-4 mt-2 sm:-mx-6 lg:-mx-8">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-[#fc2207] via-[#fe5801]/80 to-transparent sm:w-20" aria-hidden />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-[#ff8400] via-[#fe5801]/80 to-transparent sm:w-20" aria-hidden />
            <div className="overflow-hidden py-2">
              <div className="flex w-max animate-scroll hover:[animation-play-state:paused]">
                {[0, 1].map((dup) => (
                  <div
                    key={dup}
                    className="flex gap-5 pr-5 shrink-0 sm:gap-6 sm:pr-6"
                    {...(dup === 1 ? ({ 'aria-hidden': 'true' } as const) : {})}
                  >
                    <figure className="group relative aspect-[5/4] w-64 shrink-0 overflow-hidden rounded-2xl border border-white/20 bg-white/[0.07] shadow-[0_16px_44px_-14px_rgba(0,0,0,0.38)] backdrop-blur-md sm:w-72 lg:w-80">
                      <img
                        src="/assets/BurokDeck2.png"
                        alt="Vue d'ensemble de l'écosystème Burok"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t via-transparent to-transparent pointer-events-none from-black/65" />
                      <figcaption className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                        <span className="text-sm font-semibold text-white drop-shadow sm:text-base">Tout se connecte</span>
                      </figcaption>
                    </figure>
                    <figure className="group relative aspect-[5/4] w-64 shrink-0 overflow-hidden rounded-2xl border border-white/20 bg-white/[0.07] shadow-[0_16px_44px_-14px_rgba(0,0,0,0.38)] backdrop-blur-md sm:w-72 lg:w-80">
                      <img
                        src="/assets/free2.png"
                        alt="Avantages et expérience utilisateur Burok"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#fc2207]/25 via-transparent to-transparent" />
                      <figcaption className="absolute left-3 top-3 rounded-full border border-white/25 bg-black/30 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/95 backdrop-blur-sm sm:left-4 sm:top-4 sm:px-3 sm:py-1 sm:text-xs">
                        Lifestyle
                      </figcaption>
                    </figure>
                    <figure className="group relative aspect-[5/4] w-64 shrink-0 overflow-hidden rounded-2xl border border-white/20 bg-white/[0.07] shadow-[0_16px_44px_-14px_rgba(0,0,0,0.38)] backdrop-blur-md sm:w-72 lg:w-80">
                      <img
                        src="/assets/BurgersBusiness.gif"
                        alt="Univers food et business sur Burok"
                        className="object-cover w-full h-full"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t via-transparent to-transparent pointer-events-none from-black/50" />
                      <figcaption className="absolute right-3 top-3 rounded-full border border-white/25 bg-black/30 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/95 backdrop-blur-sm sm:right-4 sm:top-4 sm:px-3 sm:py-1 sm:text-xs">
                        Business & food
                      </figcaption>
                    </figure>
                    <EcosystemRotatingCarouselFigure activeIndex={ecosystemRotateIndex} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Aperçu app — captures imageappburok (avant « Découvrez Burok en action ») */}
      <section
        className="overflow-hidden relative z-10 px-4 py-24 bg-white sm:px-6 lg:px-8 lg:py-28"
        aria-labelledby="app-burok-showcase-heading"
      >
        <div className="overflow-hidden absolute inset-0 z-0 pointer-events-none" aria-hidden>
          {APP_BUROK_SHOWCASE_GRAFITI.map(({ src, className }) => (
            <img
              key={src}
              src={src}
              alt=""
              className={`object-contain select-none ${className}`}
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>

        <div className="grid relative z-10 gap-12 items-center mx-auto max-w-7xl lg:grid-cols-2 lg:gap-16">
          <div className="text-center lg:text-left">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-gray-500 sm:text-sm">
              {isEnglish ? 'The Burok experience' : "L'expérience Burok"}
            </p>
            <h2
              id="app-burok-showcase-heading"
              className="mb-5 text-4xl font-bold tracking-tight text-gray-900 md:text-5xl"
            >
              {isEnglish ? 'Built for everyday life' : "L'app telle qu'au quotidien"}
            </h2>
            <p className="mx-auto max-w-xl text-lg leading-relaxed text-gray-600 md:text-xl lg:mx-0">
              {isEnglish
                ? 'Shopping, wallet, services and food in one clean flow. A fast, mobile-first interface. Swipe through a preview of the screens and feel the vibe.'
                : "Parcours shopping, wallet, services et food : une même interface claire, pensée pour aller vite sur mobile — les écrans défilent ici en aperçu pour vous donner envie d'ouvrir Burok."}
            </p>
          </div>

          <div className="flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl">
              <div
                className="relative aspect-square w-full overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/[0.04]"
                aria-live="polite"
                aria-label={
                  isEnglish
                    ? 'Burok app previews, auto slideshow (1:1 images)'
                    : "Aperçus de l’application Burok, diaporama automatique (images 1:1)"
                }
              >
                {APP_BUROK_SHOWCASE_IMAGES.map(({ src, alt }, i) => (
                  <img
                    key={src}
                    src={src}
                    alt={alt}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1100ms] ease-in-out ${
                      i === appBurokShowcaseIndex ? 'z-[1] opacity-100' : 'z-0 opacity-0'
                    } ${i !== appBurokShowcaseIndex ? 'pointer-events-none' : ''}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="relative z-10 overflow-hidden bg-[url('/assets/Burok%20gradient.png')] bg-cover bg-center bg-no-repeat px-4 py-20 sm:px-6 lg:px-8">
        <div className="relative z-10 mx-auto max-w-6xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">
              {isEnglish ? 'See Burok in action' : 'Découvrez Burok en action'}
            </h2>
            <p className="mx-auto max-w-2xl text-xl text-white/90">
               {isEnglish
                 ? 'Explore the marketplace with thousands of products at your fingertips'
                 : "Explorez notre plateforme e-commerce avec des milliers de produits à portée de main"}
             </p>
           </div>
           
           <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
             {/* Image 1 — alternance Burokimage1 / REEF4GRG */}
             <div className="overflow-hidden relative rounded-2xl border backdrop-blur-sm group bg-white/10 border-white/20">
               <div className="aspect-[4/3] overflow-hidden relative">
                 <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-110">
                   {GALLERY_CATALOGUE_IMAGES.map(({ src, alt }, i) => (
                     <img
                       key={src}
                       src={src}
                       alt={alt}
                       className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1100ms] ease-in-out ${
                         i === catalogueGalleryImageIndex ? 'z-[1] opacity-100' : 'z-0 opacity-0'
                       }`}
                       loading={i === 0 ? 'eager' : 'lazy'}
                       decoding="async"
                     />
                   ))}
                 </div>
               </div>
               <div className="absolute inset-0 bg-gradient-to-t via-transparent to-transparent opacity-0 transition-opacity duration-300 from-black/60 group-hover:opacity-100"></div>
               <div className="absolute right-0 bottom-0 left-0 p-6 text-white transition-transform duration-300 transform translate-y-full group-hover:translate-y-0">
                <h3 className="mb-2 text-2xl font-bold tracking-tight">
                  {isEnglish ? 'Curated catalog' : 'Catalogue varié'}
                </h3>
                <p className="text-white/90">
                  {isEnglish ? 'Discover hand-picked products you’ll love' : 'Découvrez nos produits soigneusement sélectionnés'}
                </p>
               </div>
             </div>

             {/* Image 2 */}
             <div className="overflow-hidden relative rounded-2xl border backdrop-blur-sm group bg-white/10 border-white/20">
               <div className="aspect-[4/3] overflow-hidden">
                 <img 
                   src="/assets/BurokBanner.png" 
                   alt="Banner Burok" 
                   className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                 />
               </div>
               <div className="absolute inset-0 bg-gradient-to-t via-transparent to-transparent opacity-0 transition-opacity duration-300 from-black/60 group-hover:opacity-100"></div>
               <div className="absolute right-0 bottom-0 left-0 p-6 text-white transition-transform duration-300 transform translate-y-full group-hover:translate-y-0">
                 <h3 className="mb-2 text-2xl font-bold tracking-tight">
                   {isEnglish ? 'Fast delivery' : 'Livraison rapide'}
                 </h3>
                 <p className="text-white/90">
                   {isEnglish ? 'Get your orders effortlessly' : 'Recevez vos commandes en toute simplicité'}
                 </p>
               </div>
             </div>

             {/* Image 3 */}
             <div className="overflow-hidden relative rounded-2xl border backdrop-blur-sm group bg-white/10 border-white/20">
               <div className="aspect-[4/3] overflow-hidden">
                 <img 
                   src="/assets/Color rules the world_ Turning a simple street….jpg" 
                   alt="Design coloré" 
                   className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                 />
               </div>
               <div className="absolute inset-0 bg-gradient-to-t via-transparent to-transparent opacity-0 transition-opacity duration-300 from-black/60 group-hover:opacity-100"></div>
               <div className="absolute right-0 bottom-0 left-0 p-6 text-white transition-transform duration-300 transform translate-y-full group-hover:translate-y-0">
                 <h3 className="mb-2 text-2xl font-bold tracking-tight">
                   {isEnglish ? 'Secure payments' : 'Paiement sécurisé'}
                 </h3>
                 <p className="text-white/90">
                   {isEnglish ? 'Simple, secure transactions' : 'Transactions simples et sécurisées'}
                 </p>
               </div>
             </div>
           </div>
         </div>
       </section>

      {/* Agents IA Burok — clients */}
      <section
        className="overflow-hidden relative z-10 px-4 py-24 bg-white sm:px-6 lg:px-8 lg:py-28"
        aria-labelledby="agents-ia-burok-heading"
      >
        <div className="overflow-hidden absolute inset-0 z-0 pointer-events-none" aria-hidden>
          {AGENTS_IA_SECTION1_GRAFITI.map(({ src, className }) => (
            <img
              key={src}
              src={src}
              alt=""
              className={`object-contain pointer-events-none select-none ${className}`}
              loading="lazy"
              decoding="async"
            />
          ))}
          <div className="absolute inset-x-0 top-24 mx-auto h-64 max-w-3xl rounded-full bg-[#fc2207]/[0.08] blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-6xl">
          <header className="mx-auto mb-12 max-w-3xl text-center md:mb-14">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-gray-500 sm:text-sm">
              {isEnglish ? 'AI Agents' : 'Agents IA'}
            </p>
            <h2
              id="agents-ia-burok-heading"
              className="text-4xl font-bold tracking-tight text-gray-900 md:text-5xl lg:text-6xl"
            >
              {isEnglish ? 'Your' : 'Votre'} agent{' '}
              <span className="bg-gradient-to-r from-[#fc2207] to-[#fe5801] bg-clip-text text-transparent">
                Burok
              </span>
              {isEnglish ? ', built for you' : ', pensé pour vous'}
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-600 md:text-xl">
              {isEnglish
                ? 'A smart assistant on the user side. Speak naturally and it prepares transfers, shopping, bills, rides or food inside Burok. No queues, at your pace.'
                : "Un assistant intelligent côté client : vous parlez naturellement, il prépare transferts, shopping, factures, transport ou food dans l'app — sans file d'attente, à votre rythme."}
            </p>
          </header>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,0.6fr)] lg:items-stretch lg:gap-8">
            <div className="flex min-h-0 flex-col rounded-[1.75rem] border border-white/20 bg-[url('/assets/Burok%20gradient.png')] bg-cover bg-center bg-no-repeat p-4 shadow-xl ring-1 ring-white/15 sm:p-6 lg:h-full">
              <p className="mb-2 shrink-0 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70 sm:text-[11px] lg:text-left">
                {isEnglish ? 'AI Agent' : 'Agent IA'}
              </p>
              <h3 className="mb-4 text-2xl font-bold tracking-tight text-center text-white drop-shadow-sm shrink-0 sm:mb-5 sm:text-3xl lg:text-left">
                {isEnglish ? 'What it can do for you' : "Ce qu'il peut faire pour vous"}
              </h3>
              <div className="flex-1 mb-5 space-y-4 min-h-0 text-center sm:mb-6 lg:text-left">
                <p className="text-sm leading-relaxed text-white/95 sm:text-[0.9375rem] sm:leading-relaxed">
                  {isEnglish
                    ? 'Say it in one sentence. The agent understands your intent, uses context (balance, history, preferences), and opens the right flows without drowning you in menus.'
                    : "Exprimez-vous en une phrase : l'agent comprend votre demande, tient compte du contexte (solde, historique, préférences) et ouvre les bons parcours — sans vous noyer dans les menus."}
                </p>
                <p className="text-sm leading-relaxed text-white/85 sm:text-[0.9375rem] sm:leading-relaxed">
                  {isEnglish
                    ? 'Every step stays visible and editable. One conversation for transfers, shopping and bookings in Burok.'
                    : "Chaque étape reste visible et modifiable ; une seule conversation pour vos transferts, courses et réservations dans Burok."}
                </p>
              </div>
              <ul className="grid grid-cols-3 gap-2 mx-auto max-w-3xl shrink-0 sm:gap-3 lg:mx-0 lg:mt-auto">
                {AGENTS_BUROK_SERVICE_ROWS.map(({ label, Icon }) => (
                  <li
                    key={label}
                    className="flex flex-col items-center gap-2 rounded-2xl border border-gray-200/90 bg-white px-2 py-3 text-center shadow-md ring-1 ring-black/[0.04] transition duration-300 hover:bg-gray-50 hover:shadow-lg sm:px-3 sm:py-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#fc2207]/10 text-[#fc2207] ring-1 ring-[#fc2207]/15 sm:h-10 sm:w-10">
                      <Icon className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" strokeWidth={2} aria-hidden />
                    </span>
                    <span className="text-[11px] font-semibold leading-tight text-gray-900 sm:text-xs">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="group relative flex flex-col overflow-hidden rounded-[1.75rem] border border-white/20 bg-[url('/assets/Burok%20gradient.png')] bg-cover bg-center bg-no-repeat shadow-[0_28px_80px_-24px_rgba(0,0,0,0.45)] ring-1 ring-white/15">
              <div className="relative aspect-[16/10] min-h-[220px] sm:min-h-[280px]">
                <img
                  src="/assets/Service%20Client%20Burok%20GhibliOFF.webp"
                  alt="Agent IA Burok — illustration"
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="relative z-10 px-4 pt-4 pb-3 border-t border-white/20 sm:px-5 sm:pb-4 sm:pt-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70 sm:text-xs">
                  {isEnglish ? 'Your copilot' : 'Votre copilote'}
                </p>
                <h3 className="mt-1 max-w-2xl text-xl font-bold tracking-tight leading-snug text-white drop-shadow sm:text-2xl">
                  {isEnglish ? 'Smart assistant' : 'Assistant intelligent'}
                </h3>
                <p className="mt-2 max-w-2xl text-sm font-medium leading-relaxed text-white/90 sm:text-base">
                  {isEnglish
                    ? 'One conversation to trigger actions in Burok. The agent understands context and guides you to the outcome.'
                    : "Une conversation pour déclencher vos actions dans Burok : l'agent IA comprend le contexte et vous guide jusqu'au résultat."}
                </p>

                <div className="overflow-hidden relative mt-3 rounded-xl sm:mt-4" aria-label="Illustrations agent IA">
                  <div className="flex w-max animate-scroll-agents items-center gap-2.5 py-1 pr-2.5 hover:[animation-play-state:paused] sm:gap-3">
                    {[0, 1].map((dup) => (
                      <div
                        key={dup}
                        className="flex shrink-0 items-center gap-2.5 pr-2.5 sm:gap-3 sm:pr-3"
                        {...(dup === 1 ? ({ 'aria-hidden': 'true' } as const) : {})}
                      >
                        {AGENTS_BUROK_GHIBLI_IDS.map((id) => (
                          <div
                            key={`${dup}-${id}`}
                            className="overflow-hidden relative w-12 h-12 rounded-xl ring-1 shrink-0 ring-white/20 sm:h-14 sm:w-14"
                          >
                            <img
                              src={`/assets/Ghibli/${id}.webp`}
                              alt=""
                              className="object-cover w-full h-full"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Où échanger avec l&apos;agent IA — chat & WhatsApp */}
      <section
        className="overflow-hidden relative z-10 px-4 pt-2 pb-24 bg-white sm:px-6 lg:px-8 lg:pb-28"
        aria-labelledby="agent-ia-canaux-heading"
      >
        <div className="overflow-hidden absolute inset-0 z-0 pointer-events-none" aria-hidden>
          {AGENTS_IA_SECTION2_GRAFITI.map(({ src, className }) => (
            <img
              key={src}
              src={src}
              alt=""
              className={`object-contain pointer-events-none select-none ${className}`}
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>

        <div className="relative z-10 mx-auto max-w-6xl">
          <header className="mx-auto mb-10 max-w-2xl text-center sm:mb-12">
            <h2
              id="agent-ia-canaux-heading"
              className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl"
            >
              {isEnglish ? 'Where to chat with your AI agent' : 'Où parler à votre agent IA'}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-gray-600 md:text-lg">
              {isEnglish
                ? 'Same experience in Burok chat or on WhatsApp. Just ask in natural language.'
                : 'Même expérience dans le chat Burok ou sur WhatsApp : posez votre demande en langage naturel.'}
            </p>
          </header>

          <div className="grid gap-6 md:grid-cols-2 md:gap-8">
            <article className="flex h-full min-h-0 flex-col overflow-hidden rounded-[1.75rem] border border-white/20 bg-[url('/assets/Burok%20gradient.png')] bg-cover bg-center bg-no-repeat shadow-xl ring-1 ring-white/15">
              <div className="relative min-h-[360px] flex-1 sm:min-h-[420px] md:min-h-[480px]">
                <img
                  src="/assets/IMG_5760.jpeg"
                  alt={
                    isEnglish
                      ? 'Conversation with the AI agent in Burok chat'
                      : "Conversation avec l'agent IA dans le chat Burok"
                  }
                  className="object-cover object-top absolute inset-0 w-full h-full"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="px-5 py-4 border-t border-white/20 sm:px-6 sm:py-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">
                  {isEnglish ? 'In the app' : "Dans l'application"}
                </p>
                <h3 className="mt-1 text-2xl font-bold tracking-tight text-white drop-shadow-sm sm:text-3xl">
                  {isEnglish ? 'Live chat' : 'Chat en direct'}
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-white/90">
                  {isEnglish
                    ? 'Track requests, confirmations and actions in the same thread.'
                    : 'Suivi des demandes, confirmations et actions dans le même fil de discussion.'}
                </p>
              </div>
            </article>

            <article className="flex h-full min-h-0 flex-col overflow-hidden rounded-[1.75rem] border border-white/20 bg-[url('/assets/Burok%20gradient.png')] bg-cover bg-center bg-no-repeat p-5 shadow-xl ring-1 ring-white/15 sm:p-6 md:p-7">
              <div className="flex gap-3 items-start mb-5 shrink-0 sm:mb-6">
                <span className="flex justify-center items-center w-11 h-11 text-white rounded-2xl ring-1 shrink-0 bg-white/15 ring-white/25">
                  <MessageCircle className="w-5 h-5" strokeWidth={2} aria-hidden />
                </span>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">
                    WhatsApp
                  </p>
                  <h3 className="mt-1 text-2xl font-bold tracking-tight text-white drop-shadow-sm sm:text-3xl">
                    {isEnglish ? 'Same agent, different channel' : 'Même agent, autre canal'}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/90">
                    {isEnglish
                      ? "Continue the conversation on WhatsApp when it's more convenient for you."
                      : "Continuez la conversation sur WhatsApp quand c'est plus pratique pour vous."}
                  </p>
                </div>
              </div>
              <div className="relative mt-auto w-full overflow-hidden rounded-2xl ring-1 ring-white/20 aspect-[16/10] min-h-[280px] sm:min-h-[340px] md:min-h-[380px]">
                <img
                  src="/assets/MbayesurWhatsapp%20.png"
                  alt={isEnglish ? 'Burok AI agent available on WhatsApp' : 'Agent IA Burok disponible sur WhatsApp'}
                  className="object-cover absolute inset-0 w-full h-full"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </article>
          </div>
        </div>
      </section>

       {/* Mini-Apps Section */}
       <section className="relative z-10 px-4 py-20 sm:px-6 lg:px-8">
         <div className="mx-auto max-w-7xl">
           <div className="grid gap-12 items-center lg:grid-cols-2">
             {/* Text Content */}
             <div className="text-center lg:text-left">
               <div className="inline-flex justify-center items-center px-4 py-2 mb-6 text-sm font-medium text-white rounded-full border backdrop-blur-sm bg-white/20 border-white/30">
                 🚀 {isEnglish ? 'Super App' : 'Super App'}
               </div>
               <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
                 {isEnglish ? 'More than a shopping app' : "Plus qu'une app de shopping"}
               </h2>
               <p className="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-white/90 lg:mx-0">
                 {isEnglish ? (
                   <>
                     Burok is a full ecosystem that brings your favorite mini-apps into one place.
                     Access everyday services without ever leaving the app.
                   </>
                 ) : (
                   <>
                     Burok est un écosystème complet qui regroupe toutes vos applications préférées
                     en un seul endroit. Accédez à des services variés sans quitter l'app.
                   </>
                 )}
               </p>
               
               {/* Mini-Apps Grid */}
               <div className="relative z-10 grid grid-cols-2 gap-4 mb-8">
                 <div className="relative z-10 isolate rounded-xl border border-neutral-200 p-4 text-center shadow-md mix-blend-normal !bg-white">
                   <div className="mb-2 text-2xl font-bold !text-neutral-950">20+</div>
                   <div className="text-sm font-medium !text-neutral-950">
                     {isEnglish ? 'Mini-apps available' : 'Mini-apps disponibles'}
                   </div>
                 </div>
                 <div className="relative z-10 isolate rounded-xl border border-neutral-200 p-4 text-center shadow-md mix-blend-normal !bg-white">
                   <div className="mb-2 text-2xl font-bold !text-neutral-950">24/7</div>
                   <div className="text-sm font-medium !text-neutral-950">
                     {isEnglish ? 'Always on' : 'Accès continu'}
                   </div>
                 </div>
               </div>
             </div>
             
             {/* Animated Image */}
             <div className="flex justify-center lg:justify-end">
               <div className="relative w-full max-w-lg">
                 <div className="overflow-hidden rounded-3xl border backdrop-blur-sm bg-white/10 border-white/20">
                   <div className="relative min-h-[400px]">
                     {/* Image actuelle avec animation d'entrée */}
                     <img 
                       src={animatedImages[currentImageIndex]} 
                       alt="Burok Super App" 
                       className="w-full h-auto max-h-[500px] object-contain image-enter"
                       key={`current-${currentImageIndex}`}
                     />
                     
                     {/* Image suivante (préchargée) */}
                     <img 
                       src={animatedImages[(currentImageIndex + 1) % animatedImages.length]} 
                       alt="Burok Super App" 
                       className="w-full h-auto max-h-[500px] object-contain absolute top-0 left-0 opacity-0"
                       key={`next-${currentImageIndex}`}
                     />
                   </div>
                 </div>
                 
                 {/* Floating Elements */}
                 <div className="absolute -top-4 -right-4 w-8 h-8 bg-[#fe2e3c] rounded-full opacity-60 animate-bounce" style={{animationDelay: '0s'}}></div>
                 <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-[#ff4a66] rounded-full opacity-60 animate-bounce" style={{animationDelay: '1s'}}></div>
               </div>
             </div>
           </div>
           
           {/* Mini-Apps Showcase avec Onglets */}
           <div className="mt-20">
             <div className="mb-12 text-center">
               <h3 className="mb-4 text-3xl font-bold text-white">
                 {isEnglish ? 'Our mini-apps' : 'Nos Mini-Apps'}
               </h3>
               <p className="mx-auto max-w-2xl text-lg text-white/80">
                 {isEnglish ? 'Explore the full Burok ecosystem' : "Découvrez l'écosystème complet de Burok"}
               </p>
             </div>
             
             {/* Onglets Client/Business */}
             <div className="flex justify-center mb-12">
               <div className="inline-flex p-1 rounded-2xl border backdrop-blur-sm bg-white/10 border-white/20">
                 <button
                   onClick={() => setActiveTab('client')}
                   className={`px-8 py-3 rounded-xl font-medium transition-all duration-300 ${
                     activeTab === 'client'
                       ? 'bg-white text-gray-900 shadow-lg scale-105'
                       : 'text-white/70 hover:text-white hover:scale-105'
                   }`}
                 >
                   {isEnglish ? '👥 Users' : '👥 Client'}
                 </button>
                 <button
                   onClick={() => setActiveTab('business')}
                   className={`px-8 py-3 rounded-xl font-medium transition-all duration-300 ${
                     activeTab === 'business'
                       ? 'bg-white text-gray-900 shadow-lg scale-105'
                       : 'text-white/70 hover:text-white hover:scale-105'
                   }`}
                 >
                   {isEnglish ? '🏢 Business' : '🏢 Business'}
                 </button>
               </div>
             </div>

            {/* Filtre par type */}
            <div className="mb-10 flex flex-wrap items-center justify-center gap-2">
              {(
                [
                  'All',
                  'Finance',
                  'Shopping',
                  'Transport',
                  'Logement',
                  'Service',
                  'Social',
                  'Autres',
                ] as const
              ).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setActiveMiniAppType(type)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                    activeMiniAppType === type
                      ? 'bg-white text-gray-900 shadow-lg'
                      : 'border border-white/20 bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {miniAppTypeLabel(type)}
                </button>
              ))}
            </div>
             
             {/* Grille des applications selon l'onglet actif */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {(activeTab === 'client' ? FALLBACK_APPS : FALLBACK_BUSINESS_APPS)
                .filter((app: { show: boolean }) => app.show)
                .filter((app: { type?: AppType }) => {
                  if (activeMiniAppType === 'All') return true;
                  return (app.type ?? 'Autres') === activeMiniAppType;
                })
                 .map((app) => (
                   <div 
                     key={app.id} 
                     className="cursor-pointer group"
                     onClick={() => handleAppClick(app)}
                   >
                    <div className="overflow-hidden relative rounded-xl border backdrop-blur-sm transition-all duration-300 bg-white/10 border-white/20 hover:scale-105 hover:bg-white/20 p-3 sm:p-4">
                      <div className="mx-auto mb-2 aspect-square w-12 overflow-hidden rounded-lg bg-white/10 sm:mb-3 sm:w-full">
                         <img 
                           src={app.logo} 
                           alt={app.name} 
                           className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
                         />
                       </div>
                        <h4 className="mb-1 text-sm font-semibold text-center text-white">
                          {appDisplayName(app)}
                        </h4>
                       <p className="text-xs leading-tight text-center text-white/70 line-clamp-2">
                         {appDisplayDescription(app)}
                       </p>
                     </div>
                   </div>
                 ))}
             </div>
             
             {/* Section "Bientôt d'autres à venir" */}
             <div className="mt-16 text-center">
               <div className="inline-flex gap-3 items-center px-6 py-4 rounded-2xl border backdrop-blur-sm bg-white/10 border-white/20">
                 <div className="w-3 h-3 bg-gradient-to-r from-[#fc2207] to-[#fe5801] rounded-full animate-pulse"></div>
                 <span className="text-lg font-medium text-white">
                   {isEnglish ? "🚀 More mini-apps coming soon..." : "🚀 Bientôt d'autres applications à venir..."}
                 </span>
                 <div className="w-3 h-3 bg-gradient-to-r from-[#fc2207] to-[#fe5801] rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
               </div>
             </div>
           </div>
         </div>
       </section>

       {/* Additional Images Section */}
       <section className="relative z-10 px-4 py-20 sm:px-6 lg:px-8">
         <div className="mx-auto max-w-6xl">
           <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">
              {isEnglish ? 'Our product universe' : 'Notre univers produits'}
            </h2>
            <p className="mx-auto max-w-2xl text-xl text-white/80">
              {isEnglish
                ? 'Discover thousands of quality products for every need'
                : 'Découvrez des milliers de produits de qualité pour tous vos besoins'}
            </p>
           </div>
           
           <div className="grid gap-8 md:grid-cols-2">
             {/* Large Image 1 */}
             <div className="overflow-hidden relative rounded-3xl border backdrop-blur-sm group bg-white/10 border-white/20">
               <div className="aspect-[3/2] overflow-hidden">
                 <img 
                   src="/assets/HappywomanBlue.png" 
                   alt="Design moderne" 
                   className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
                 />
               </div>
               <div className="absolute inset-0 bg-gradient-to-t via-transparent to-transparent opacity-0 transition-opacity duration-500 from-black/80 group-hover:opacity-100"></div>
               <div className="absolute right-0 bottom-0 left-0 p-8 text-white transition-transform duration-500 transform translate-y-4 group-hover:translate-y-0">
                <h3 className="mb-3 text-2xl font-bold">
                  {isEnglish ? 'Fashion & lifestyle' : 'Mode & Lifestyle'}
                </h3>
                <p className="text-lg text-white/90">
                  {isEnglish ? 'Stay ahead of the latest trends' : 'Restez à la pointe des dernières tendances'}
                </p>
               </div>
             </div>

             {/* Large Image 2 */}
             <div className="overflow-hidden relative rounded-3xl border backdrop-blur-sm group bg-white/10 border-white/20">
               <div className="aspect-[3/2] overflow-hidden">
                 <img 
                   src="/assets/Dan Bel.jpeg" 
                   alt="Innovation technologique" 
                   className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
                 />
               </div>
               <div className="absolute inset-0 bg-gradient-to-t via-transparent to-transparent opacity-0 transition-opacity duration-500 from-black/80 group-hover:opacity-100"></div>
               <div className="absolute right-0 bottom-0 left-0 p-8 text-white transition-transform duration-500 transform translate-y-4 group-hover:translate-y-0">
                 <h3 className="mb-3 text-2xl font-bold">
                   {isEnglish ? 'Electronics & tech' : 'Électronique & Tech'}
                 </h3>
                 <p className="text-lg text-white/90">
                   {isEnglish ? 'The latest innovations' : 'Les dernières innovations technologiques'}
                 </p>
               </div>
             </div>
           </div>

           {/* Small Images Row */}
           <div className="grid gap-6 mt-12 md:grid-cols-4">
             <div className="overflow-hidden relative rounded-2xl border group bg-white/10 border-white/20">
               <div className="overflow-hidden relative aspect-square">
                 <img 
                   src="/assets/Womansmiling.jpeg" 
                   alt="Style unique" 
                   className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                 />
                 <img
                   src="/assets/Burok%20White%20logo.png"
                   alt=""
                   className="pointer-events-none absolute bottom-3 left-3 z-10 h-8 w-auto object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] md:bottom-4 md:left-4 md:h-10"
                   aria-hidden
                 />
               </div>
             </div>
             <div className="overflow-hidden relative rounded-2xl border group bg-white/10 border-white/20">
               <div className="overflow-hidden relative aspect-square">
                 <img 
                   src="/assets/cacurry.jpeg" 
                   alt="Créativité" 
                   className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                 />
                 <img
                   src="/assets/Burok%20White%20logo.png"
                   alt=""
                   className="pointer-events-none absolute bottom-3 left-3 z-10 h-8 w-auto object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] md:bottom-4 md:left-4 md:h-10"
                   aria-hidden
                 />
               </div>
             </div>
             <div className="overflow-hidden relative rounded-2xl border group bg-white/10 border-white/20">
               <div className="overflow-hidden relative aspect-square">
                 <img 
                   src="/assets/bleustylywoman.jpg" 
                   alt="Innovation" 
                   className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                 />
                 <img
                   src="/assets/Burok%20White%20logo.png"
                   alt=""
                   className="pointer-events-none absolute bottom-3 left-3 z-10 h-8 w-auto object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] md:bottom-4 md:left-4 md:h-10"
                   aria-hidden
                 />
               </div>
             </div>
             <div className="overflow-hidden relative rounded-2xl border group bg-white/10 border-white/20">
               <div className="overflow-hidden relative aspect-square">
                 <img 
                   src="/assets/Welcome6.jpg" 
                   alt="Excellence" 
                   className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                 />
                 <img
                   src="/assets/Burok%20White%20logo.png"
                   alt=""
                   className="pointer-events-none absolute bottom-3 left-3 z-10 h-8 w-auto object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] md:bottom-4 md:left-4 md:h-10"
                   aria-hidden
                 />
               </div>
             </div>
           </div>
         </div>
       </section>

      {/* CTA Section — fond = bandeau orange parent ; carte intérieure blanche */}
      <section className="relative z-10 px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="group relative overflow-hidden rounded-3xl border border-gray-200 bg-white p-10 shadow-xl ring-1 ring-black/[0.04] sm:p-12">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#fc2207]/[0.06] to-[#ff8400]/[0.06] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <div className="relative z-10">
              <h2 className="mb-6 text-4xl font-bold tracking-tight text-gray-900 whitespace-nowrap md:text-5xl">
                {isEnglish ? 'Ready to join Burok?' : 'Prêt à rejoindre Burok ?'}
              </h2>
              <p className="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-gray-600">
                {isEnglish
                  ? 'Join thousands of users who already discovered a new way to shop and get things done with Burok.'
                  : "Rejoignez des milliers d'utilisateurs qui ont déjà découvert une nouvelle expérience de shopping avec Burok."}
              </p>

              <div className="space-y-6">
                <DownloadButtons />

                <div className="flex flex-wrap gap-y-3 gap-x-4 justify-center text-gray-500">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm">
                      {isEnglish ? '1M+ active users' : '1M+ utilisateurs actifs'}
                    </span>
                  </div>
                  <div className="hidden w-px h-4 bg-gray-200 sm:block" />
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"
                      style={{ animationDelay: '1s' }}
                    />
                    <span className="text-sm">4.9★ App Store</span>
                  </div>
                  <div className="hidden w-px h-4 bg-gray-200 sm:block" />
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"
                      style={{ animationDelay: '2s' }}
                    />
                    <span className="text-sm">
                      {isEnglish ? '99.9% uptime' : '99.9% disponibilité'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-12 text-gray-900 bg-white border-t border-gray-200">
        <div className="px-4 mx-auto max-w-4xl sm:px-6 lg:px-8">
          {/* Main Footer Content - Horizontal Layout */}
          <div className="flex flex-col gap-8 justify-between items-center mb-8 lg:flex-row">
            {/* Left Side - Logo & Brand */}
            <div className="flex gap-4 items-center">
              <img
                src="/assets/BUROKORANGELOGOSMALLER.png"
                alt="Burok"
                className="object-contain w-auto h-11 sm:h-12"
              />
              <div>
                <h3 className="mb-1 text-lg font-bold text-gray-900">
                  {isEnglish ? 'Try Burok' : 'Essayer Burok'}
                </h3>
                <p className="text-sm text-gray-600">
                  {isEnglish
                    ? 'The app that upgrades how you shop and live'
                    : "L'application mobile qui transforme votre façon de shopper"}
                </p>
              </div>
            </div>
            
            {/* Center - Download Button */}
            <button
              onClick={handleDownloadClick}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#fc2207] to-[#fe5801] text-white font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 shadow-md"
            >
              <span className="mr-2">🚀</span>
              {isEnglish ? 'Download' : 'Télécharger'}
            </button>
          </div>
          
          {/* Links Row */}
          <div className="flex flex-wrap gap-8 justify-center items-center mb-8 text-sm">
            <a href="/cgu" className="text-gray-600 transition-colors duration-300 hover:text-gray-900">
              {isEnglish ? 'Terms' : 'CGU'}
            </a>
            <div className="w-1 h-1 bg-gray-300 rounded-full" />
            <a href="/privacy" className="text-gray-600 transition-colors duration-300 hover:text-gray-900">
              {isEnglish ? 'Privacy' : 'Confidentialité'}
            </a>
            <div className="w-1 h-1 bg-gray-300 rounded-full" />
            <a href="/data/delete" className="text-gray-600 transition-colors duration-300 hover:text-gray-900">
              {isEnglish ? 'Delete my data' : 'Supprimer mes données'}
            </a>
            <div className="w-1 h-1 bg-gray-300 rounded-full" />
            <a
              href="mailto:support@burok.app"
              className="text-gray-600 transition-colors duration-300 hover:text-gray-900"
            >
              {isEnglish ? 'Support' : 'Support'}
            </a>
          </div>

          {/* Copyright */}
          <div className="pt-8 text-center border-t border-gray-200">
            <p className="text-sm text-gray-500">
              &copy; 2025 Burok. {isEnglish ? 'All rights reserved.' : 'Tous droits réservés.'}
            </p>
          </div>
        </div>
       </footer>

       {/* Download Modal */}
       <DownloadModal 
         isOpen={isDownloadModalOpen} 
         onClose={() => setIsDownloadModalOpen(false)} 
       />

       {/* App Details Modal */}
       {isAppModalOpen && selectedApp && (
         <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 backdrop-blur-sm bg-black/60"
             onClick={closeAppModal}
           />
           
           {/* Modal Content */}
           <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border backdrop-blur-sm bg-white/95 border-white/30 shadow-2xl">
             {/* Close Button */}
             <button
               onClick={closeAppModal}
               className="absolute top-4 right-4 z-10 p-2 text-white rounded-full border backdrop-blur-sm transition-all duration-300 bg-white/20 border-white/30 hover:bg-white/30"
              title={isEnglish ? 'Close modal' : 'Fermer le modal'}
              aria-label={isEnglish ? 'Close modal' : 'Fermer le modal'}
             >
               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
               </svg>
             </button>
             
             {/* Modal Body */}
             <div className="flex flex-col h-full lg:flex-row">
                                {/* Left Side - Banner Image */}
                 <div className="relative lg:w-2/3">
                   <div className="aspect-[3/4] overflow-hidden">
                     <img 
                       src={selectedApp.banner} 
                       alt={selectedApp.name} 
                       className="object-cover w-full h-full"
                     />
                   </div>
                 
                 {/* Gradient Overlay */}
                 <div className="absolute inset-0 bg-gradient-to-t via-transparent to-transparent from-black/60" />
                 
                 {/* App Logo on Banner */}
                 <div className="absolute bottom-4 left-4">
                   <div className="flex gap-3 items-center p-3 rounded-2xl border backdrop-blur-sm bg-white/20 border-white/30">
                     <img 
                       src={selectedApp.logo} 
                       alt={selectedApp.name} 
                       className="object-cover w-12 h-12 rounded-xl"
                     />
                     <div>
                       <h2 className="text-xl font-bold text-white">{selectedApp.name}</h2>
                       <div className="text-sm text-white/80">
                        {activeTab === 'client'
                          ? isEnglish
                            ? 'Consumer app'
                            : 'Application Client'
                          : isEnglish
                            ? 'Business app'
                            : 'Application Business'}
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
               
               {/* Right Side - Content */}
               <div className="flex flex-col justify-center p-8 lg:w-1/3">
                 <div className="mb-6">
                   <h3 className="mb-4 text-2xl font-bold text-gray-900">
                    {appDisplayName(selectedApp)}
                   </h3>
                   <p className="leading-relaxed text-gray-700">
                    {appDisplayDescription(selectedApp)}
                   </p>
                 </div>
                 
                 {/* Action Buttons */}
                 <div className="space-y-3">
                   <button className="w-full px-6 py-3 bg-gradient-to-r from-[#fc2207] to-[#fe5801] text-white font-medium rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300">
                      <span className="flex items-center justify-center gap-2">
                       <img
                         src="/assets/BUROKORANGELOGOSMALLER.png"
                         alt=""
                         className="h-5 w-auto object-contain drop-shadow-sm"
                         aria-hidden
                       />
                      {isEnglish ? 'Open in Burok' : 'Ouvrir dans Burok'}
                     </span>
                   </button>
                   <button className="px-6 py-3 w-full font-medium text-gray-700 rounded-xl border border-gray-300 backdrop-blur-sm transition-all duration-300 bg-white/10 hover:bg-white/20">
                     {isEnglish ? 'ℹ️ Learn more' : 'ℹ️ En savoir plus'}
                   </button>
                 </div>
                 
                 {/* App Info */}
                 <div className="pt-6 mt-6 border-t border-gray-200">
                   <div className="flex justify-between items-center text-sm text-gray-500">
                    <span>{isEnglish ? 'Type' : 'Type'}</span>
                     <span className="font-medium">
                      {activeTab === 'client'
                        ? isEnglish
                          ? 'Consumer app'
                          : 'Application Client'
                        : isEnglish
                          ? 'Business app'
                          : 'Application Business'}
                     </span>
                   </div>
                   <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
                    <span>{isEnglish ? 'Status' : 'Statut'}</span>
                     <span className="flex gap-1 items-center">
                       <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      {isEnglish ? 'Available' : 'Disponible'}
                     </span>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* My Space Modal */}
       {isMySpaceModalOpen && (
         <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 backdrop-blur-sm bg-black/60"
             onClick={closeMySpaceModal}
           />
           
           {/* Modal Content */}
           <div className="overflow-hidden relative w-full max-w-2xl rounded-3xl border shadow-2xl backdrop-blur-sm bg-white/95 border-white/30">
             {/* Close Button */}
             <button
               onClick={closeMySpaceModal}
               className="absolute top-4 right-4 z-10 p-2 text-white rounded-full border backdrop-blur-sm transition-all duration-300 bg-white/20 border-white/30 hover:bg-white/30"
              title={isEnglish ? 'Close modal' : 'Fermer le modal'}
              aria-label={isEnglish ? 'Close modal' : 'Fermer le modal'}
             >
               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
               </svg>
             </button>
             
             {/* Modal Body */}
             <div className="p-8 text-center">
               {/* Icon */}
               <div className="mx-auto mb-6 w-20 h-20 bg-gradient-to-br from-[#fc2207] to-[#fe5801] rounded-full flex items-center justify-center shadow-lg">
                 <LayoutGrid className="w-10 h-10 text-white" />
               </div>
               
               {/* Title */}
               <h2 className="mb-4 text-3xl font-bold text-gray-900">
                 My Space
               </h2>
               
               {/* Message */}
               <p className="mb-8 text-lg leading-relaxed text-gray-700">
                {isEnglish
                  ? 'Coming soon. You’ll be able to sign in from the web to access your personal Burok space.'
                  : "Bientôt ! Tu pourras te connecter depuis l'espace web pour accéder à ton espace personnel Burok."}
               </p>
               
               {/* Features */}
               <div className="grid gap-4 mb-8 md:grid-cols-2">
                 <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                   <div className="mb-2 text-2xl">🔐</div>
                  <h3 className="mb-1 text-lg font-bold tracking-tight text-gray-900">
                    {isEnglish ? 'Secure sign-in' : 'Connexion sécurisée'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isEnglish ? 'Secure access to your account' : 'Accès sécurisé à ton compte'}
                  </p>
                 </div>
                 <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                   <div className="mb-2 text-2xl">📱</div>
                  <h3 className="mb-1 text-lg font-bold tracking-tight text-gray-900">
                    {isEnglish ? 'Sync' : 'Synchronisation'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isEnglish ? 'Your data stays synced with the app' : "Données synchronisées avec l'app"}
                  </p>
                 </div>
               </div>
               
               {/* Action Button */}
               <button
                 onClick={closeMySpaceModal}
                 className="px-8 py-3 bg-gradient-to-r from-[#fc2207] to-[#fe5801] text-white font-medium rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
               >
                {isEnglish ? 'Got it!' : 'Compris !'}
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Styles pour les animations des images */}
       <style dangerouslySetInnerHTML={{
         __html: `
           @keyframes slideInFromRight {
             from {
               opacity: 0;
               transform: translateX(30px) scale(0.9);
             }
             to {
               opacity: 1;
               transform: translateX(0) scale(1);
             }
           }
           
           @keyframes slideOutToLeft {
             from {
               opacity: 1;
               transform: translateX(0) scale(1);
             }
             to {
               opacity: 0;
               transform: translateX(-30px) scale(0.9);
             }
           }
           
           @keyframes zoomIn {
             from {
               opacity: 0;
               transform: scale(0.8) rotate(-2deg);
             }
             to {
               opacity: 1;
               transform: scale(1) rotate(0deg);
             }
           }
           
           .image-enter {
             animation: zoomIn 1s ease-out forwards;
           }
           
           .image-exit {
             animation: slideOutToLeft 1s ease-out forwards;
           }
         `
       }} />




      </div>
    </div>
   );
 };

const App: React.FC = () => {
  const shortLinkHost = typeof window !== 'undefined' && isShortLinkHostname();
  const appDeeplinkHost = typeof window !== 'undefined' && isAppDeeplinkHostname();

  return (
    <Router>
      <LanguageFromPathSync />
      {shortLinkHost ? (
        <Routes>
          <Route path="/*" element={<ShortLinkOpenPage />} />
        </Routes>
      ) : appDeeplinkHost ? (
        <Routes>
          <Route path="/*" element={<AppDeepLinkOpenPage />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/en" element={<Home />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/en/cgu" element={<CGU />} />
          <Route path="/cgu/service-usage" element={<ServiceUsage />} />
          <Route path="/en/cgu/service-usage" element={<ServiceUsage />} />
          <Route path="/cgu/rights-responsibilities" element={<RightsResponsibilities />} />
          <Route path="/en/cgu/rights-responsibilities" element={<RightsResponsibilities />} />
          <Route path="/cgu/security-data-protection" element={<SecurityDataProtection />} />
          <Route path="/en/cgu/security-data-protection" element={<SecurityDataProtection />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/en/privacy" element={<Privacy />} />
          <Route path="/data/delete" element={<DataDelete />} />
          <Route path="/en/data/delete" element={<DataDelete />} />
          <Route path="/support" element={<Support />} />
          <Route path="/en/support" element={<Support />} />
          <Route path="/link" element={<LinkHomePage />} />
          <Route path="/link/:shortCode" element={<LinkOpenPage />} />
          <Route path="/pay" element={<PayFallbackPage />} />
        </Routes>
      )}
    </Router>
  );
};

function LanguageFromPathSync() {
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  const shouldBeEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const nextLanguage = shouldBeEnglish ? 'en' : 'fr';

  if (language !== nextLanguage) setLanguage(nextLanguage);
  return null;
}

export default App;

